// src/components/PatientResultSections.jsx
//
// The two plain-language sections at the heart of a triage result — "What this
// means" (with the flagged markers as colour-coded pills) and "What to do next"
// (with the personalised recommendation). Extracted from PatientTriageResultPage
// so the doctor portal can show a doctor exactly what the patient was told,
// from the same code, instead of a second copy that drifts.

import React, { useState } from 'react';
import Icon from './Icon';
import CardLink from './CardLink';
import Sparkline from './Sparkline';
import { matchMarkerHistory } from '../utils/markerHistory';

// ── Risk level configuration ──────────────────────────────────────────────

export const RISK_CONFIG = {
  LOW: {
    label: 'Your health looks good',
    badge: 'Green — Healthy range',
    color: 'var(--green)',
    bg:    'var(--green-bg)',
    icon:  '✅',
    headline: 'Your results are in a healthy range',
    meaning:
      'Most of your markers are within normal limits for your age and profile. ' +
      'This is great news — your body is responding well to your current lifestyle.',
    actions: [
      'Keep doing what you\'re doing — your routine is working.',
      'Come back in 6 months for your next check-up.',
      'If anything feels off before then, don\'t hesitate to chat with us.',
    ],
    // Doctor-portal wording: same content, addressed to the clinician.
    doctorMeaning:
      "Most markers are within normal limits for the patient's age and profile. Their current lifestyle appears to be working.",
    doctorActions: [
      "No intervention needed; encourage the patient to continue their current routine.",
      "Schedule the next routine check-up in about 6 months.",
      "The patient has been told to contact the care team sooner if anything changes.",
    ],
    chatPrompt: 'My results look healthy — what can I do to stay this way?',
  },
  MODERATE: {
    label: 'Some areas need attention',
    badge: 'Amber — Review recommended',
    color: 'var(--amber)',
    bg:    'var(--amber-bg)',
    icon:  '⚠️',
    headline: 'A few markers are outside the ideal range',
    meaning:
      'This doesn\'t mean something is seriously wrong — but it does mean your body ' +
      'is giving us an early signal. Catching this now is exactly what preventive care is for.',
    actions: [
      'Your health navigator will review these results and be in touch.',
      'In the meantime, small changes to diet, sleep, and exercise can make a real difference.',
      'Ask Previu what the specific markers mean and what you can do.',
    ],
    // Doctor-portal wording: same content, addressed to the clinician.
    doctorMeaning:
      "A few markers are outside the ideal range. Not acutely concerning, but an early signal that is worth addressing while it is still preventable.",
    doctorActions: [
      "Review the flagged markers and agree a plan with the patient; the navigator has been asked to follow up.",
      "Reinforce diet, sleep and exercise changes, and accept or reject the suggested lifestyle items.",
      "Recheck the flagged markers at the next visit.",
    ],
    chatPrompt: 'My results show some markers are out of range — what does this mean for me?',
  },
  HIGH: {
    label: 'Action recommended',
    badge: 'Red — Review needed',
    color: 'var(--red)',
    bg:    'var(--red-bg)',
    icon:  '🔴',
    headline: 'Some of your results need prompt attention',
    meaning:
      'One or more markers are significantly outside the healthy range. ' +
      'This is not an emergency — but it does need a doctor\'s review. ' +
      'Your care team has been notified and will be in touch shortly.',
    actions: [
      'A doctor or navigator will contact you within 24 hours to discuss next steps.',
      'If you feel unwell or have symptoms, please call 112 or go to your nearest clinic.',
      'Do not start or stop any medication without speaking to a doctor first.',
    ],
    // Doctor-portal wording: same content, addressed to the clinician.
    doctorMeaning:
      "One or more markers are significantly outside the healthy range. Not an emergency, but physician review is needed. The care team has been notified.",
    doctorActions: [
      "Review the report and contact the patient within 24 hours to discuss next steps; the patient has been told to expect this.",
      "Ask about symptoms and advise emergency care (112) or the nearest clinic if the patient is unwell.",
      "Advise the patient not to start or stop any medication before you have reviewed the results with them.",
    ],
    chatPrompt: 'My results show some high-risk markers — what should I know?',
    urgent: true,
  },
  CRITICAL: {
    label: 'Urgent review required',
    badge: 'Critical — Contact your doctor',
    color: 'var(--red)',
    bg:    'var(--red-bg)',
    icon:  '🚨',
    headline: 'Your results require urgent attention',
    meaning:
      'One or more critical markers were detected. Your care team has been alerted. ' +
      'Please do not ignore this — prompt follow-up is important for your health.',
    actions: [
      'A doctor will contact you urgently. Please keep your phone nearby.',
      'If you feel unwell, have chest pain, difficulty breathing, or other acute symptoms, call 112 immediately.',
      'Do not wait — contact your doctor directly if you haven\'t heard within 2 hours.',
    ],
    // Doctor-portal wording: same content, addressed to the clinician.
    doctorMeaning:
      "One or more critical markers were detected and the care team has been alerted. Prompt follow-up is needed.",
    doctorActions: [
      "Contact the patient urgently; they have been told to keep their phone nearby.",
      "Screen for acute symptoms such as chest pain or difficulty breathing, and direct to emergency care (112) if present.",
      "Acknowledge the critical escalation; the patient has been told to contact a doctor directly if they have not heard within 2 hours.",
    ],
    chatPrompt: 'My results show critical markers — what are the immediate steps?',
    urgent: true,
  },
};

// ── Components ────────────────────────────────────────────────────────────

export const NORMAL_STATUSES = new Set(['normal', 'optimal']);

const MARKER_PILL_COLORS = {
  optimal:  { bg: 'var(--green-bg)',  color: 'var(--green)',  label: '✓ Optimal'  },
  normal:   { bg: 'var(--green-bg)',  color: 'var(--green)',  label: '✓ Normal'   },
  elevated: { bg: 'var(--amber-bg)',  color: 'var(--amber)',  label: '↑ Elevated' },
  low:      { bg: 'var(--blue-bg)',   color: 'var(--blue)',   label: '↓ Low'      },
  high:     { bg: 'var(--red-bg)',    color: 'var(--red)',    label: '↑ High'     },
  critical: { bg: 'var(--red-bg)',    color: 'var(--red)',    label: '⚠ Critical' },
};

// A flagged marker's own trend, expanded inline below its row (same card,
// not a separate page or modal) — same Sparkline component and layout the
// Current Markers tab's PatientMarkerCard uses for this, just triggered from
// the results screen's "Key findings" list instead.
// Hover text for a trend dot: draw date + value.
const drawLabel = (p) => {
  const d = p.date ? new Date(p.date) : null;
  const when = d && !Number.isNaN(d.getTime())
    ? d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
    : 'Draw date unknown';
  return `${when} · ${p.val} ${p.unit || ''}`.trim();
};

function MarkerTrend({ points, uid }) {
  const vals = points.map(p => p.val);
  const latest = points[points.length - 1];
  const cfg = MARKER_PILL_COLORS[latest.status?.toLowerCase()] || MARKER_PILL_COLORS.normal;
  return (
    <div style={{ marginTop: 8, paddingTop: 10, borderTop: '1px solid var(--border)' }}>
      <Sparkline points={vals} color={cfg.color} width={260} height={40} uid={uid} labels={points.map(drawLabel)} />
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: 11, color: 'var(--text-muted)' }}>
        <span>{points[0].val} {points[0].unit}</span>
        <span style={{ color: cfg.color, fontWeight: 500 }}>→ {latest.val} {latest.unit}</span>
      </div>
      <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>
        {points.length} draw{points.length !== 1 ? 's' : ''} with this marker
      </div>
    </div>
  );
}

// Clickable when a trend (2+ draws) exists for this marker — tapping expands
// its sparkline in the same card, right below the marker's own row.
function MarkerPill({ name, status, trendPoints }) {
  const [expanded, setExpanded] = useState(false);
  const cfg = MARKER_PILL_COLORS[status?.toLowerCase()] || { bg: 'var(--bg-subtle)', color: 'var(--text-muted)', label: status };
  const hasTrend = trendPoints && trendPoints.length >= 2;

  return (
    <div style={{
      padding: '10px 14px', borderRadius: 8,
      background: 'var(--bg-surface)', border: '1px solid var(--border)',
      cursor: hasTrend ? 'pointer' : 'default',
    }}
      role={hasTrend ? 'button' : undefined}
      tabIndex={hasTrend ? 0 : undefined}
      aria-expanded={hasTrend ? expanded : undefined}
      aria-label={hasTrend ? `${name} trend` : undefined}
      onClick={hasTrend ? (() => setExpanded(v => !v)) : undefined}
      onKeyDown={hasTrend ? (e => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpanded(v => !v); }
      }) : undefined}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <span style={{ fontSize: 13, color: 'var(--text-primary)', fontFamily: 'var(--font-body)', fontWeight: 500 }}>
          {name}
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{
            fontSize: 11, fontWeight: 700, padding: '2px 9px',
            borderRadius: 20, background: cfg.bg, color: cfg.color,
            fontFamily: 'var(--font-body)', letterSpacing: '0.04em', whiteSpace: 'nowrap',
          }}>
            {cfg.label}
          </span>
          {hasTrend && (
            <Icon name="chevronRight" size={12} style={{
              color: 'var(--text-muted)', flexShrink: 0, transition: 'transform 0.15s',
              transform: expanded ? 'rotate(90deg)' : 'none',
            }} />
          )}
        </span>
      </div>
      {expanded && hasTrend && (
        <MarkerTrend points={trendPoints} uid={name.replace(/[^a-zA-Z0-9]/g, '')} />
      )}
    </div>
  );
}


// Markers array out of a triage report's claudeRawResponse (JSON string).
export function parseReportMarkers(result) {
  if (!result?.claudeRawResponse) return [];
  try {
    const raw = typeof result.claudeRawResponse === 'string'
      ? JSON.parse(result.claudeRawResponse) : result.claudeRawResponse;
    return raw?.markers || raw?.sub_scores?.markers || [];
  } catch { return []; }
}

// audience: 'patient' (default) or 'doctor' — same data, different voice.
// initialVisible: when set, only that many key findings show until the reader
// taps "Show N more" — keeps a long flagged list from burying the sections
// below. Unset shows them all (the doctor view).
export function WhatThisMeans({ result, markers, markerHistory, allMarkersTo, audience = 'patient', initialVisible }) {
  const doctor = audience === 'doctor';
  const [showAll, setShowAll] = useState(false);
  const cfg = RISK_CONFIG[(result.consensusRiskLevel || 'LOW').toUpperCase()] || RISK_CONFIG.LOW;
  const flaggedMarkers = markers.filter(m => {
    const status = (m.status || m.severity || 'normal').toLowerCase();
    return !NORMAL_STATUSES.has(status);
  });
  return (
    <section style={{
      background: 'var(--bg-surface)', borderRadius: 12, padding: '20px',
      marginBottom: 16, border: '1px solid var(--border)',
    }}>
      <h2 style={{
        fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 700,
        color: 'var(--text-primary)', margin: '0 0 10px',
        textTransform: 'uppercase', letterSpacing: '0.06em',
      }}>
        What this means
      </h2>
      <p style={{
        fontSize: 14, color: 'var(--text-secondary)',
        fontFamily: 'var(--font-body)', lineHeight: 1.8, margin: 0,
      }}>
        {doctor ? cfg.doctorMeaning : cfg.meaning}
      </p>

      {/* Key findings — the markers this report flagged, as colour-coded
          pills (same vocabulary as MarkerPill/the Markers tab) instead
          of a prose paragraph naming which markers to worry about. The
          full list — flagged and normal alike — lives on the Markers
          tab now (see the link below), not duplicated here. */}
      {flaggedMarkers.length > 0 && (
        <div style={{
          marginTop: 14, padding: '12px 14px', borderRadius: 8,
          background: 'var(--bg-subtle)',
          borderLeft: `3px solid ${cfg.color}`,
        }}>
          <div style={{
            fontSize: 11, fontWeight: 700, color: 'var(--text-muted)',
            textTransform: 'uppercase', letterSpacing: '0.08em',
            fontFamily: 'var(--font-body)', marginBottom: 8,
          }}>
            {doctor ? 'Key findings from this report' : 'Key findings from your report'}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {(showAll || initialVisible == null ? flaggedMarkers : flaggedMarkers.slice(0, initialVisible)).map((m, i) => {
              const markerName = m.name || m.marker;
              return (
                <MarkerPill
                  key={markerName || i}
                  name={markerName}
                  status={m.status || m.severity || 'normal'}
                  trendPoints={matchMarkerHistory(markerHistory, markerName)}
                />
              );
            })}
          </div>
          {initialVisible != null && flaggedMarkers.length > initialVisible && (
            <button
              type="button"
              aria-expanded={showAll}
              onClick={() => setShowAll(v => !v)}
              style={{
                marginTop: 10, padding: 0, border: 'none', background: 'none',
                color: 'var(--text-primary)', fontSize: 12.5, fontWeight: 700,
                fontFamily: 'var(--font-body)', cursor: 'pointer',
              }}
            >
              {showAll ? 'Show less' : `Show ${flaggedMarkers.length - initialVisible} more`}
            </button>
          )}
        </div>
      )}

      {allMarkersTo && (
        <div style={{ marginTop: 14 }}>
          <CardLink to={allMarkersTo}>See all your current markers</CardLink>
        </div>
      )}
    </section>
  );
}

export function WhatToDoNext({ result, title = 'What to do next', audience = 'patient' }) {
  const doctor = audience === 'doctor';
  const cfg = RISK_CONFIG[(result.consensusRiskLevel || 'LOW').toUpperCase()] || RISK_CONFIG.LOW;
  return (
    <section style={{
      background: 'var(--bg-surface)', borderRadius: 12, padding: '20px',
      marginBottom: 16, border: '1px solid var(--border)',
    }}>
      <h2 style={{
        fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 700,
        color: 'var(--text-primary)', margin: '0 0 12px',
        textTransform: 'uppercase', letterSpacing: '0.06em',
      }}>
        {title}
      </h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {(doctor ? cfg.doctorActions : cfg.actions).map((action, i) => (
          <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <div style={{
              width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
              background: cfg.bg, color: cfg.color,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 11, fontWeight: 700, fontFamily: 'var(--font-mono)',
              marginTop: 1,
            }}>
              {i + 1}
            </div>
            <p style={{
              fontSize: 14, color: 'var(--text-secondary)',
              fontFamily: 'var(--font-body)', lineHeight: 1.6, margin: 0,
            }}>
              {action}
            </p>
          </div>
        ))}
      </div>

      {/* Recommendation from Claude — if available */}
      {result.claudeRecommendation && (
        <div style={{
          marginTop: 16, padding: '12px 14px', borderRadius: 8,
          background: 'var(--purple-bg)',
          borderLeft: '3px solid var(--purple)',
        }}>
          <div style={{
            fontSize: 11, fontWeight: 700, color: 'var(--purple)',
            textTransform: 'uppercase', letterSpacing: '0.08em',
            fontFamily: 'var(--font-body)', marginBottom: 6,
          }}>
            {doctor ? 'Patient-specific recommendation' : 'Personalised recommendation'}
          </div>
          <p style={{
            fontSize: 13, color: 'var(--text-secondary)',
            fontFamily: 'var(--font-body)', lineHeight: 1.7, margin: 0,
          }}>
            {result.claudeRecommendation}
          </p>
        </div>
      )}
    </section>
  );
}
